import {
  keepPreviousData,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import {
  GetSubscriptions,
  GetSubscription,
  PostSubscription,
  PutSubscription,
  DeleteSubscription,
  GetSubscriptionTags,
  GetSubscriptionTag,
  PostSubscriptionTag,
  PutSubscriptionTag,
  DeleteSubscriptionTag,
  GetSubscriptionCategories,
  GetSubscriptionCategory,
  PostSubscriptionCategory,
  PutSubscriptionCategory,
  DeleteSubscriptionCategory,
  GetSubscriptionEstimateReadiness,
  GetSubscriptionSettings,
  PutSubscriptionSettings,
  GetSubscriptionFreezePolicy,
  PutSubscriptionFreezePolicy,
  PostSubscriptionFreezePolicyOverride,
  PutSubscriptionFreezePolicyOverride,
  DeleteSubscriptionFreezePolicyOverride,
  GetSubscriptionMakeupPolicy,
  PutSubscriptionMakeupPolicy,
  PostSubscriptionMakeupPolicyOverride,
  PutSubscriptionMakeupPolicyOverride,
  DeleteSubscriptionMakeupPolicyOverride,
  GetSubscriptionRolloverPolicy,
  PutSubscriptionRolloverPolicy,
  PostSubscriptionRolloverPolicyOverride,
  PutSubscriptionRolloverPolicyOverride,
  DeleteSubscriptionRolloverPolicyOverride,
  GetSubscriptionUnpaidCancellationPolicy,
  PutSubscriptionUnpaidCancellationPolicy,
  PostSubscriptionUnpaidCancellationPolicyOverride,
  PutSubscriptionUnpaidCancellationPolicyOverride,
  DeleteSubscriptionUnpaidCancellationPolicyOverride,
  GetSubscriptionCommitmentPolicy,
  PutSubscriptionCommitmentPolicy,
  PostSubscriptionCommitmentPolicyOverride,
  PutSubscriptionCommitmentPolicyOverride,
  DeleteSubscriptionCommitmentPolicyOverride,
  GetSubscriptionFreezeRequests,
  PostApproveSubscriptionFreezeRequest,
  PostRejectSubscriptionFreezeRequest,
} from "@/server-types";
import { del, get, post, put } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";

type QueryOpts<TData> = Omit<
  UseQueryOptions<TData, AxiosError, TData>,
  "queryKey" | "queryFn"
> & { displayNotification?: boolean };

type MutationOpts<TData, TVariables> = Omit<
  UseMutationOptions<TData, AxiosError, TVariables>,
  "mutationFn" | "onError"
> & {
  displayNotification?: boolean;
  onError?: (
    error: AxiosError,
    variables: TVariables,
    context: unknown,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
};

const COMMITMENT_POLICY_KEY = ["subscriptionCommitmentPolicy"];

function invalidateCommitmentPolicy() {
  queryClient.invalidateQueries({ queryKey: COMMITMENT_POLICY_KEY });
}

/**
 * GET /subscription
 */
export const useGetSubscriptions = (
  query?: GetSubscriptions["query"],
  enabled?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["getSubscriptions", query],
    queryFn: () => get<GetSubscriptions>("/subscription", query),
    placeholderData: keepPreviousData,
    enabled: enabled ?? true,
  });
};

/**
 * GET /subscription/:id
 */
export const useGetSubscription = (
  subscriptionId: number,
  options?: { enabled?: boolean },
) => {
  return useCustomQuery({
    queryKey: ["subscription", subscriptionId],
    queryFn: () => get<GetSubscription>(`/subscription/${subscriptionId}`),
    enabled: (options?.enabled ?? true) && subscriptionId !== -1,
  });
};

/**
 * POST /subscription
 */
export const usePostSubscription = () => {
  return useCustomMutation({
    mutationFn: (body: PostSubscription["body"]) =>
      post<PostSubscription>("/subscription", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getSubscriptions"],
      });
    },
  });
};

/**
 * PUT /subscription/:id
 */
export const usePutSubscription = () => {
  return useCustomMutation({
    mutationFn: ({
      subscriptionId,
      body,
    }: {
      subscriptionId: number;
      body: PutSubscription["body"];
    }) => put<PutSubscription>(`/subscription/${subscriptionId}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["subscription", variables.subscriptionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["getSubscriptions"],
      });
    },
  });
};

/**
 * DELETE /subscription/:id
 */
export const useDeleteSubscription = () => {
  return useCustomMutation({
    mutationFn: (subscriptionId: number) =>
      del<DeleteSubscription>(`/subscription/${subscriptionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getSubscriptions"],
      });
    },
  });
};

/**
 * GET /subscription/tag
 */
export const useGetSubscriptionTags = () => {
  return useCustomQuery({
    queryKey: ["subscriptionTags"],
    queryFn: () => get<GetSubscriptionTags>("/subscription/tag"),
  });
};

/**
 * GET /subscription/tag/:id
 */
export const useGetSubscriptionTag = (tagId: number) => {
  return useCustomQuery({
    queryKey: ["subscriptionTag", tagId],
    queryFn: () => get<GetSubscriptionTag>(`/subscription/tag/${tagId}`),
    enabled: tagId !== -1,
  });
};

/**
 * POST /subscription/tag
 */
export const usePostSubscriptionTag = () => {
  return useCustomMutation({
    mutationFn: (body: PostSubscriptionTag["body"]) =>
      post<PostSubscriptionTag>("/subscription/tag", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionTags"],
      });
    },
  });
};

/**
 * PUT /subscription/tag/:id
 */
export const usePutSubscriptionTag = () => {
  return useCustomMutation({
    mutationFn: ({
      tagId,
      body,
    }: {
      tagId: number;
      body: PutSubscriptionTag["body"];
    }) => put<PutSubscriptionTag>(`/subscription/tag/${tagId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionTags"],
      });
    },
  });
};

/**
 * DELETE /subscription/tag/:id
 */
export const useDeleteSubscriptionTag = () => {
  return useCustomMutation({
    mutationFn: (tagId: number) =>
      del<DeleteSubscriptionTag>(`/subscription/tag/${tagId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionTags"],
      });
    },
  });
};

/**
 * GET /subscription/category
 */
export const useGetSubscriptionCategories = () => {
  return useCustomQuery({
    queryKey: ["subscriptionCategories"],
    queryFn: () => get<GetSubscriptionCategories>("/subscription/category"),
  });
};

/**
 * GET /subscription/category/:id
 */
export const useGetSubscriptionCategory = (categoryId: number) => {
  return useCustomQuery({
    queryKey: ["subscriptionCategory", categoryId],
    queryFn: () =>
      get<GetSubscriptionCategory>(`/subscription/category/${categoryId}`),
    enabled: categoryId !== -1,
  });
};

/**
 * POST /subscription/category
 */
export const usePostSubscriptionCategory = () => {
  return useCustomMutation({
    mutationFn: (body: PostSubscriptionCategory["body"]) =>
      post<PostSubscriptionCategory>("/subscription/category", body),
  });
};

/**
 * DELETE /subscription/category/:id
 */
export const useDeleteSubscriptionCategory = () => {
  return useCustomMutation({
    mutationFn: (categoryId: number) =>
      del<DeleteSubscriptionCategory>(`/subscription/category/${categoryId}`),
  });
};

/**
 * PUT /subscription/category/:id
 */
export const usePutSubscriptionCategory = () => {
  return useCustomMutation({
    mutationFn: ({
      categoryId,
      body,
    }: {
      categoryId: number;
      body: PutSubscriptionCategory["body"];
    }) =>
      put<PutSubscriptionCategory>(
        `/subscription/category/${categoryId}`,
        body,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionCategory", variables.categoryId],
      });
      queryClient.invalidateQueries({
        queryKey: ["subscriptionCategories"],
      });
    },
  });
};

/**
 * GET /subscription/settings
 */
export const useGetSubscriptionSettings = () => {
  return useCustomQuery({
    queryKey: ["subscriptionSettings"],
    queryFn: () => get<GetSubscriptionSettings>("/subscription/settings"),
  });
};

/**
 * GET /subscription/settings/estimate-readiness
 */
export const useGetSubscriptionEstimateReadiness = (
  query?: GetSubscriptionEstimateReadiness["query"],
  enabled?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["subscriptionEstimateReadiness", query],
    queryFn: () =>
      get<GetSubscriptionEstimateReadiness>(
        "/subscription/settings/estimate-readiness",
        query,
      ),
    enabled: enabled ?? true,
  });
};

/**
 * PUT /subscription/settings
 */
export const usePutSubscriptionSettings = () => {
  return useCustomMutation({
    mutationFn: (body: PutSubscriptionSettings["body"]) =>
      put<PutSubscriptionSettings>("/subscription/settings", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionSettings"],
      });
    },
  });
};

/**
 * GET /subscription/freeze-policy
 */
export const useGetSubscriptionFreezePolicy = () => {
  return useCustomQuery({
    queryKey: ["subscriptionFreezePolicy"],
    queryFn: () =>
      get<GetSubscriptionFreezePolicy>("/subscription/freeze-policy"),
  });
};

/**
 * PUT /subscription/freeze-policy
 */
export const usePutSubscriptionFreezePolicy = () => {
  return useCustomMutation({
    mutationFn: (body: PutSubscriptionFreezePolicy["body"]) =>
      put<PutSubscriptionFreezePolicy>("/subscription/freeze-policy", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionFreezePolicy"],
      });
    },
  });
};

/**
 * POST /subscription/freeze-policy/override
 */
export const usePostSubscriptionFreezePolicyOverride = () => {
  return useCustomMutation({
    mutationFn: (body: PostSubscriptionFreezePolicyOverride["body"]) =>
      post<PostSubscriptionFreezePolicyOverride>(
        "/subscription/freeze-policy/override",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionFreezePolicy"],
      });
    },
  });
};

/**
 * PUT /subscription/freeze-policy/override/:overrideId
 */
export const usePutSubscriptionFreezePolicyOverride = () => {
  return useCustomMutation({
    mutationFn: ({
      overrideId,
      body,
    }: {
      overrideId: number;
      body: PutSubscriptionFreezePolicyOverride["body"];
    }) =>
      put<PutSubscriptionFreezePolicyOverride>(
        `/subscription/freeze-policy/override/${overrideId}`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionFreezePolicy"],
      });
    },
  });
};

/**
 * DELETE /subscription/freeze-policy/override/:overrideId
 */
export const useDeleteSubscriptionFreezePolicyOverride = () => {
  return useCustomMutation({
    mutationFn: (overrideId: number) =>
      del<DeleteSubscriptionFreezePolicyOverride>(
        `/subscription/freeze-policy/override/${overrideId}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionFreezePolicy"],
      });
    },
  });
};

/**
 * GET /subscription/makeup-policy
 */
export const useGetSubscriptionMakeupPolicy = () => {
  return useCustomQuery({
    queryKey: ["subscriptionMakeupPolicy"],
    queryFn: () =>
      get<GetSubscriptionMakeupPolicy>("/subscription/makeup-policy"),
  });
};

/**
 * PUT /subscription/makeup-policy
 */
export const usePutSubscriptionMakeupPolicy = () => {
  return useCustomMutation({
    mutationFn: (body: PutSubscriptionMakeupPolicy["body"]) =>
      put<PutSubscriptionMakeupPolicy>("/subscription/makeup-policy", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionMakeupPolicy"] });
    },
  });
};

/**
 * POST /subscription/makeup-policy/override
 */
export const usePostSubscriptionMakeupPolicyOverride = () => {
  return useCustomMutation({
    mutationFn: (body: PostSubscriptionMakeupPolicyOverride["body"]) =>
      post<PostSubscriptionMakeupPolicyOverride>(
        "/subscription/makeup-policy/override",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionMakeupPolicy"] });
    },
  });
};

/**
 * PUT /subscription/makeup-policy/override/:overrideId
 */
export const usePutSubscriptionMakeupPolicyOverride = () => {
  return useCustomMutation({
    mutationFn: ({
      overrideId,
      body,
    }: {
      overrideId: number;
      body: PutSubscriptionMakeupPolicyOverride["body"];
    }) =>
      put<PutSubscriptionMakeupPolicyOverride>(
        `/subscription/makeup-policy/override/${overrideId}`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionMakeupPolicy"] });
    },
  });
};

/**
 * DELETE /subscription/makeup-policy/override/:overrideId
 */
export const useDeleteSubscriptionMakeupPolicyOverride = () => {
  return useCustomMutation({
    mutationFn: (overrideId: number) =>
      del<DeleteSubscriptionMakeupPolicyOverride>(
        `/subscription/makeup-policy/override/${overrideId}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionMakeupPolicy"] });
    },
  });
};

/**
 * GET /subscription/rollover-policy
 */
export const useGetSubscriptionRolloverPolicy = () => {
  return useCustomQuery({
    queryKey: ["subscriptionRolloverPolicy"],
    queryFn: () =>
      get<GetSubscriptionRolloverPolicy>("/subscription/rollover-policy"),
  });
};

/**
 * PUT /subscription/rollover-policy
 */
export const usePutSubscriptionRolloverPolicy = () => {
  return useCustomMutation({
    mutationFn: (body: PutSubscriptionRolloverPolicy["body"]) =>
      put<PutSubscriptionRolloverPolicy>("/subscription/rollover-policy", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionRolloverPolicy"],
      });
    },
  });
};

/**
 * POST /subscription/rollover-policy/override
 */
export const usePostSubscriptionRolloverPolicyOverride = () => {
  return useCustomMutation({
    mutationFn: (body: PostSubscriptionRolloverPolicyOverride["body"]) =>
      post<PostSubscriptionRolloverPolicyOverride>(
        "/subscription/rollover-policy/override",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionRolloverPolicy"],
      });
    },
  });
};

/**
 * PUT /subscription/rollover-policy/override/:overrideId
 */
export const usePutSubscriptionRolloverPolicyOverride = () => {
  return useCustomMutation({
    mutationFn: ({
      overrideId,
      body,
    }: {
      overrideId: number;
      body: PutSubscriptionRolloverPolicyOverride["body"];
    }) =>
      put<PutSubscriptionRolloverPolicyOverride>(
        `/subscription/rollover-policy/override/${overrideId}`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionRolloverPolicy"],
      });
    },
  });
};

/**
 * DELETE /subscription/rollover-policy/override/:overrideId
 */
export const useDeleteSubscriptionRolloverPolicyOverride = () => {
  return useCustomMutation({
    mutationFn: (overrideId: number) =>
      del<DeleteSubscriptionRolloverPolicyOverride>(
        `/subscription/rollover-policy/override/${overrideId}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionRolloverPolicy"],
      });
    },
  });
};

/**
 * GET /subscription/unpaid-cancellation-policy
 */
export const useGetSubscriptionUnpaidCancellationPolicy = () => {
  return useCustomQuery({
    queryKey: ["subscriptionUnpaidCancellationPolicy"],
    queryFn: () =>
      get<GetSubscriptionUnpaidCancellationPolicy>(
        "/subscription/unpaid-cancellation-policy",
      ),
  });
};

/**
 * PUT /subscription/unpaid-cancellation-policy
 */
export const usePutSubscriptionUnpaidCancellationPolicy = () => {
  return useCustomMutation({
    mutationFn: (body: PutSubscriptionUnpaidCancellationPolicy["body"]) =>
      put<PutSubscriptionUnpaidCancellationPolicy>(
        "/subscription/unpaid-cancellation-policy",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionUnpaidCancellationPolicy"],
      });
    },
  });
};

/**
 * POST /subscription/unpaid-cancellation-policy/override
 */
export const usePostSubscriptionUnpaidCancellationPolicyOverride = () => {
  return useCustomMutation({
    mutationFn: (
      body: PostSubscriptionUnpaidCancellationPolicyOverride["body"],
    ) =>
      post<PostSubscriptionUnpaidCancellationPolicyOverride>(
        "/subscription/unpaid-cancellation-policy/override",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionUnpaidCancellationPolicy"],
      });
    },
  });
};

/**
 * PUT /subscription/unpaid-cancellation-policy/override/:overrideId
 */
export const usePutSubscriptionUnpaidCancellationPolicyOverride = () => {
  return useCustomMutation({
    mutationFn: ({
      overrideId,
      body,
    }: {
      overrideId: number;
      body: PutSubscriptionUnpaidCancellationPolicyOverride["body"];
    }) =>
      put<PutSubscriptionUnpaidCancellationPolicyOverride>(
        `/subscription/unpaid-cancellation-policy/override/${overrideId}`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionUnpaidCancellationPolicy"],
      });
    },
  });
};

/**
 * DELETE /subscription/unpaid-cancellation-policy/override/:overrideId
 */
export const useDeleteSubscriptionUnpaidCancellationPolicyOverride = () => {
  return useCustomMutation({
    mutationFn: (overrideId: number) =>
      del<DeleteSubscriptionUnpaidCancellationPolicyOverride>(
        `/subscription/unpaid-cancellation-policy/override/${overrideId}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionUnpaidCancellationPolicy"],
      });
    },
  });
};

/**
 * GET /subscription/commitment-policy
 */
export const useGetSubscriptionCommitmentPolicy = (
  options?: QueryOpts<GetSubscriptionCommitmentPolicy["response"]>,
) => {
  return useCustomQuery({
    queryKey: COMMITMENT_POLICY_KEY,
    queryFn: () =>
      get<GetSubscriptionCommitmentPolicy>("/subscription/commitment-policy"),
    ...options,
  });
};

/**
 * PUT /subscription/commitment-policy
 */
export const usePutSubscriptionCommitmentPolicy = (
  options?: MutationOpts<
    PutSubscriptionCommitmentPolicy["response"],
    PutSubscriptionCommitmentPolicy["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PutSubscriptionCommitmentPolicy["body"]) =>
      put<PutSubscriptionCommitmentPolicy>(
        "/subscription/commitment-policy",
        body,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateCommitmentPolicy();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

/**
 * POST /subscription/commitment-policy/override
 */
export const usePostSubscriptionCommitmentPolicyOverride = (
  options?: MutationOpts<
    PostSubscriptionCommitmentPolicyOverride["response"],
    PostSubscriptionCommitmentPolicyOverride["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PostSubscriptionCommitmentPolicyOverride["body"]) =>
      post<PostSubscriptionCommitmentPolicyOverride>(
        "/subscription/commitment-policy/override",
        body,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateCommitmentPolicy();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

/**
 * PUT /subscription/commitment-policy/override/:overrideId
 */
export const usePutSubscriptionCommitmentPolicyOverride = (
  options?: MutationOpts<
    PutSubscriptionCommitmentPolicyOverride["response"],
    {
      overrideId: number;
      body: PutSubscriptionCommitmentPolicyOverride["body"];
    }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({
      overrideId,
      body,
    }: {
      overrideId: number;
      body: PutSubscriptionCommitmentPolicyOverride["body"];
    }) =>
      put<PutSubscriptionCommitmentPolicyOverride>(
        `/subscription/commitment-policy/override/${overrideId}`,
        body,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateCommitmentPolicy();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

/**
 * DELETE /subscription/commitment-policy/override/:overrideId
 */
export const useDeleteSubscriptionCommitmentPolicyOverride = (
  options?: MutationOpts<
    DeleteSubscriptionCommitmentPolicyOverride["response"],
    number
  >,
) => {
  return useCustomMutation({
    mutationFn: (overrideId: number) =>
      del<DeleteSubscriptionCommitmentPolicyOverride>(
        `/subscription/commitment-policy/override/${overrideId}`,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateCommitmentPolicy();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

/**
 * GET /customer-subscription/freeze-requests
 */
export const useGetSubscriptionFreezeRequests = (
  query?: GetSubscriptionFreezeRequests["query"],
) => {
  return useCustomQuery({
    queryKey: ["subscriptionFreezeRequests", query],
    queryFn: () =>
      get<GetSubscriptionFreezeRequests>(
        "/customer-subscription/freeze-requests",
        query,
      ),
    placeholderData: keepPreviousData,
  });
};

/**
 * POST /customer-subscription/freeze-requests/:id/approve
 */
export const useApproveSubscriptionFreezeRequest = () => {
  return useCustomMutation({
    mutationFn: ({
      scheduledFreezeId,
      body,
    }: {
      scheduledFreezeId: number;
      body: PostApproveSubscriptionFreezeRequest["body"];
    }) =>
      post<PostApproveSubscriptionFreezeRequest>(
        `/customer-subscription/freeze-requests/${scheduledFreezeId}/approve`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionFreezeRequests"],
      });
    },
  });
};

/**
 * POST /customer-subscription/freeze-requests/:id/reject
 */
export const useRejectSubscriptionFreezeRequest = () => {
  return useCustomMutation({
    mutationFn: ({
      scheduledFreezeId,
      body,
    }: {
      scheduledFreezeId: number;
      body: PostRejectSubscriptionFreezeRequest["body"];
    }) =>
      post<PostRejectSubscriptionFreezeRequest>(
        `/customer-subscription/freeze-requests/${scheduledFreezeId}/reject`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subscriptionFreezeRequests"],
      });
    },
  });
};
